'use client';

import React, { useEffect } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import { cn, formatNaira, formatTicketNumber, vaultSpring } from '../lib/utils';
import { useHoverPosition, useViewport } from '../hooks';

export interface TicketData {
  id: string;
  ticketNumber: number;
  propertyId: string;
  propertyTitle: string;
  propertyImage?: string;
  purchaseDate: string;
  price: number;
  status: 'active' | 'drawn' | 'won' | 'lost';
}

export interface TicketCardProps {
  ticket: TicketData;
  onClick?: () => void;
  className?: string;
}

/**
 * Desktop: Foil-shader 3D tilt card
 * Mobile: Wallet-style compact card
 */
export const TicketCard: React.FC<TicketCardProps> = ({
  ticket,
  onClick,
  className,
}) => {
  const { isDesktop } = useViewport();
  const { ref, isHovered, relativePosition } = useHoverPosition<HTMLDivElement>();

  // 3D tilt effect for desktop
  const rotateX = useSpring(useMotionValue(0), vaultSpring);
  const rotateY = useSpring(useMotionValue(0), vaultSpring);

  useEffect(() => {
    if (isDesktop && isHovered) {
      rotateX.set(relativePosition.y * -10);
      rotateY.set(relativePosition.x * 10);
    } else {
      rotateX.set(0);
      rotateY.set(0);
    }
  }, [isDesktop, isHovered, relativePosition, rotateX, rotateY]);

  const statusColors = {
    active: 'border-gold-500/50 bg-gold-500/10',
    drawn: 'border-text-muted/30 bg-text-muted/10',
    won: 'border-vault-success/50 bg-vault-success/10',
    lost: 'border-vault-error/30 bg-vault-error/10',
  };

  const statusLabels = {
    active: 'Active',
    drawn: 'Drawn',
    won: 'Winner!',
    lost: 'Not Selected',
  };

  // Desktop: Full 3D card with foil effect
  if (isDesktop) {
    return (
      <motion.div
        ref={ref}
        className={cn(
          'relative cursor-pointer perspective-1000',
          className
        )}
        style={{
          rotateX,
          rotateY,
          transformStyle: 'preserve-3d',
        }}
        whileHover={{ scale: 1.02, z: 20 }}
        whileTap={{ scale: 0.98 }}
        onClick={onClick}
      >
        {/* Card Body */}
        <div className={cn(
          'relative overflow-hidden rounded-vault-lg border',
          'bg-gradient-to-br from-surface-1 via-surface-2 to-surface-1',
          statusColors[ticket.status]
        )}>
          {/* Foil Overlay */}
          <div 
            className="absolute inset-0 foil-effect opacity-30 pointer-events-none"
            style={{
              backgroundPosition: `${50 + relativePosition.x * 50}% ${50 + relativePosition.y * 50}%`,
            }}
          />

          {/* Content */}
          <div className="relative p-6">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs text-text-muted uppercase tracking-wider mb-1">Ticket</p>
                <p className="font-mono text-2xl font-bold text-gold-500 tabular-nums">
                  {formatTicketNumber(ticket.ticketNumber)}
                </p>
              </div>
              <span className={cn(
                'status-badge',
                ticket.status === 'active' && 'status-active',
                ticket.status === 'drawn' && 'status-closed',
                ticket.status === 'won' && 'status-drawn',
                ticket.status === 'lost' && 'bg-vault-error/20 text-vault-error border-vault-error/30'
              )}>
                {statusLabels[ticket.status]}
              </span>
            </div>

            {/* Property Info */}
            <div className="flex gap-4 mb-4">
              {ticket.propertyImage && (
                <div className="w-20 h-14 rounded-lg overflow-hidden bg-surface-2 flex-shrink-0">
                  <img 
                    src={ticket.propertyImage} 
                    alt="" 
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-display text-sm font-medium text-text-primary truncate">
                  {ticket.propertyTitle}
                </p>
                <p className="text-xs text-text-muted mt-1">
                  Purchased {new Date(ticket.purchaseDate).toLocaleDateString()}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-4 border-t border-white/5">
              <div>
                <p className="text-xs text-text-muted">Ticket Price</p>
                <p className="font-mono text-lg font-semibold text-text-primary tabular-nums">
                  {formatNaira(ticket.price)}
                </p>
              </div>
              <div className="w-8 h-8 rounded-full bg-gold-500/20 flex items-center justify-center">
                <svg className="w-4 h-4 text-gold-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Perforated Edge Effect */}
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-8 bg-surface-0 rounded-r-full" />
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-8 bg-surface-0 rounded-l-full" />
        </div>
      </motion.div>
    );
  }

  // Mobile: Compact wallet-style card
  return (
    <motion.div
      className={cn(
        'relative overflow-hidden rounded-vault border p-4',
        'bg-surface-1',
        statusColors[ticket.status],
        'active:scale-[0.98] transition-transform',
        className
      )}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
    >
      <div className="flex items-center gap-3">
        {/* Ticket Icon */}
        <div className="w-12 h-12 rounded-full bg-gold-500/20 flex items-center justify-center flex-shrink-0">
          <span className="font-mono text-sm font-bold text-gold-500">
            #{ticket.ticketNumber.toString().slice(-3)}
          </span>
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-text-primary truncate">
            {ticket.propertyTitle}
          </p>
          <p className="text-xs text-text-muted">
            {formatTicketNumber(ticket.ticketNumber)}
          </p>
        </div>

        {/* Status */}
        <span className={cn(
          'status-badge text-xs',
          ticket.status === 'active' && 'status-active',
          ticket.status === 'won' && 'status-drawn',
        )}>
          {statusLabels[ticket.status]}
        </span>
      </div>
    </motion.div>
  );
};

/**
 * Horizontal scrollable ticket stack for mobile
 */
export interface TicketStackProps {
  tickets: TicketData[];
  onTicketClick?: (ticket: TicketData) => void;
  className?: string;
}

export const TicketStack: React.FC<TicketStackProps> = ({
  tickets,
  onTicketClick,
  className,
}) => {
  const { isDesktop } = useViewport();

  if (isDesktop) {
    // Grid layout for desktop
    return (
      <div className={cn('grid grid-cols-2 xl:grid-cols-3 gap-6', className)}>
        {tickets.map((ticket) => (
          <TicketCard
            key={ticket.id}
            ticket={ticket}
            onClick={() => onTicketClick?.(ticket)}
          />
        ))}
      </div>
    );
  }

  // Horizontal scroll for mobile
  return (
    <div className={cn('relative', className)}>
      <div className="flex gap-3 overflow-x-auto snap-x-mandatory pb-4 hide-scrollbar -mx-4 px-4">
        {tickets.map((ticket) => (
          <div key={ticket.id} className="snap-center flex-shrink-0 w-[85%]">
            <TicketCard
              ticket={ticket}
              onClick={() => onTicketClick?.(ticket)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
